/****************
 * WHEN THE SERVER CUT THE LIST, SAY SO. M-50.
 *
 * Guillaume, 2026-09-22: "no list should be shortened, ever, if a list is too
 * long and cause problem, we display part of it and add a button 'show more'
 * at the bottom."
 *
 * Five endpoints answer that question already, on response headers:
 *
 *   X-Limit-Applied   how many rows we actually served
 *   X-Limit-Clamped   your ASK was out of range and we reduced it
 *   X-More-Rows       there are older rows behind the ones you got
 *
 * Nothing in either app read any of them, so a list that was cut looked
 * exactly like a list that was complete -- which is the whole failure: "All
 * Orders (500)" on an account with 1,300 of them.
 *
 * The headers cannot travel in the body: those endpoints return a bare array
 * and every client parses it as one. So fetchApi records them here, keyed by
 * the path it asked for, and a screen reads the last answer for its own path.
 *
 * This is a record of the LAST response per path, not a cache. A screen reads
 * it while rendering the rows that came back with it.
 ****************/

import i18n from '@/i18n';
import { intlLocale } from '@/lib/format';

export interface ListTruncation {
  /** Rows the server actually returned, when it said. */
  applied?: number;
  /** True when the limit we asked for was reduced. */
  clamped: boolean;
  /** True when rows exist beyond the ones returned. */
  moreRows: boolean;
}

const lastSeen = new Map<string, ListTruncation>();

/** The path without its query string: one row per endpoint, not per filter. */
function keyOf(endpoint: string): string {
  return endpoint.split('?')[0];
}

/** Called by fetchApi on every response. */
export function recordListTruncation(endpoint: string, res: Response): void {
  const appliedHeader = res.headers.get('X-Limit-Applied');
  const clamped = res.headers.get('X-Limit-Clamped');
  const more = res.headers.get('X-More-Rows');
  /*
   * A response that said NOTHING records nothing.
   *
   * This read Number(header) first and checked isFinite, and Number(null) is
   * 0, which is finite -- so every response from every endpoint that sets none
   * of these recorded a claim of "not clamped, no more rows". That is the one
   * thing this module must never say on no evidence. Caught by its own test.
   */
  if (appliedHeader === null && clamped === null && more === null) return;
  const applied = Number(appliedHeader);
  lastSeen.set(keyOf(endpoint), {
    applied: appliedHeader !== null && Number.isFinite(applied) && applied > 0 ? applied : undefined,
    // The clamp header carries the cap as a string on some endpoints and
    // "true" on others. Either way its presence IS the answer.
    clamped: Boolean(clamped),
    moreRows: more === 'true',
  });
}

/**
 * What the last response for this path said about being cut. Undefined when
 * that path has not answered yet, or said nothing -- which a caller must treat
 * as "no claim", never as "the list is complete".
 */
export function listTruncationFor(endpoint: string): ListTruncation | undefined {
  return lastSeen.get(keyOf(endpoint));
}

/**
 * The sentence to show under a list, or null when there is nothing to say.
 *
 * Deliberately not phrased as an error. A capped list is ordinary; what is not
 * ordinary is a capped list that looks complete.
 */
export function truncationNotice(endpoint: string): string | null {
  const t = listTruncationFor(endpoint);
  if (!t) return null;
  if (t.moreRows) {
    return t.applied
      ? i18n.t('messages:truncation.showingRecent', { n: t.applied.toLocaleString(intlLocale()) })
      : i18n.t('messages:truncation.olderExist');
  }
  if (t.clamped && t.applied) {
    return i18n.t('messages:truncation.capped', { n: t.applied.toLocaleString(intlLocale()) });
  }
  return null;
}

/** Testing only: forget everything recorded so far. */
export function resetListTruncation(): void {
  lastSeen.clear();
}
